/**
 * Timezone utilities for Hammer Head web.
 * Uses moment-timezone; all conversions use IANA time zone identifiers only.
 * Aligns with backend (checkScheduleConflict, backfillScheduleUtc) and mobile.
 *
 * Spec: hammerhead-design/specs/time-zone-support
 *
 * SAVE: Date/time entered on the schedule screen is interpreted in the session
 * time zone (schedule-screen selector or user profile TZ), converted to UTC,
 * and stored as startDateTime/endDateTime. DB always holds UTC.
 *
 * DISPLAY: All timestamps are shown in the user's profile time zone
 * (User.userTimezone), or on the schedule screen the session-level selector
 * overrides for that screen only. Use getScheduleTimeDisplayInTz(schedule, userTimezone)
 * or getScheduleTimeDisplayInLocal(schedule, userTimezone).
 *
 * DST: Conversions use the schedule date so Daylight Saving is correct.
 */

import moment from "moment-timezone";

/** Default time zone for new sites and schedules when none is set. */
export const DEFAULT_SITE_TIMEZONE = "America/Los_Angeles";

/** Default time zone for user profile (display). Aligns with backend and mobile. */
export const DEFAULT_USER_TIMEZONE = "America/Los_Angeles";

/**
 * Ensure a datetime string is parsed as UTC (GraphQL/APIs sometimes omit "Z").
 * @param {string} utcIso - ISO-like string from API
 * @returns {string} String that moment.utc() will unambiguously parse as UTC
 */
export function normalizeUtcIso(utcIso) {
  if (!utcIso || typeof utcIso !== "string") return utcIso;
  const s = utcIso.trim();
  if (s.endsWith("Z") || /[+-]\d{2}:?\d{2}$/.test(s)) return s;
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s)) return s.replace(/(\.\d{3})?$/, "$1Z");
  return s;
}

/**
 * Format a UTC ISO datetime string for display in a given IANA time zone.
 * Parses as UTC first so DST is correct; normalizes API strings that omit "Z".
 * @param {string} utcIso - ISO 8601 string (e.g. from API startDateTime/endDateTime)
 * @param {string} ianaTimezone - IANA identifier (e.g. "America/Los_Angeles")
 * @param {string} [format] - moment format; default "M/D/YYYY, h:mm A"
 * @returns {string} Formatted string in the given time zone, or "" if invalid
 */
export function formatUtcInTz(utcIso, ianaTimezone, format = "M/D/YYYY, h:mm A") {
  if (!utcIso || !ianaTimezone) return "";
  const normalized = normalizeUtcIso(utcIso);
  const m = moment.utc(normalized).tz(ianaTimezone);
  return m.isValid() ? m.format(format) : "";
}

/**
 * Parse local date + time in a given IANA time zone and return UTC ISO string.
 * @param {string} dateStr - Date "YYYY-MM-DD"
 * @param {string} timeStr - Time in "hh:mm A" (12-hour) or moment-parsable form
 * @param {string} ianaTimezone - IANA identifier
 * @returns {string|null} UTC ISO string or null if invalid
 */
export function parseLocalToUtc(dateStr, timeStr, ianaTimezone) {
  if (!dateStr || !timeStr || !ianaTimezone) return null;
  const combined = `${dateStr} ${timeStr}`;
  const m = moment.tz(combined, "YYYY-MM-DD hh:mm A", ianaTimezone);
  if (!m.isValid()) return null;
  return m.utc().toISOString();
}

/**
 * Get display start/end time strings for a schedule in the given time zone (user profile or session override).
 * Prefers startDateTime/endDateTime (UTC) when present; falls back to timeStart/timeEnd.
 * Does not use Schedule.timeZone or Site.timeZone for display (spec: time-zone-support WEB-5).
 * @param {{ startDateTime?: string, endDateTime?: string, timeStart?: string, timeEnd?: string }} schedule
 * @param {string} ianaTimezone - IANA time zone (e.g. user profile or schedule-screen session override)
 * @param {string} [timeFormat] - moment format for time; default "h:mm A"
 * @returns {{ start: string, end: string }}
 */
export function getScheduleTimeDisplayInTz(schedule, ianaTimezone, timeFormat = "h:mm A") {
  const tz = ianaTimezone || DEFAULT_USER_TIMEZONE;
  if (schedule?.startDateTime && schedule?.endDateTime) {
    return {
      start: formatUtcInTz(schedule.startDateTime, tz, timeFormat),
      end: formatUtcInTz(schedule.endDateTime, tz, timeFormat),
    };
  }
  const startStr =
    schedule?.timeStart != null && schedule?.timeStart !== ""
      ? moment(schedule.timeStart, ["HH:mm", "HH:mm:ss", "h:mm A", "hh:mm A", moment.ISO_8601]).format(timeFormat)
      : "";
  const endStr =
    schedule?.timeEnd != null && schedule?.timeEnd !== ""
      ? moment(schedule.timeEnd, ["HH:mm", "HH:mm:ss", "h:mm A", "hh:mm A", moment.ISO_8601]).format(timeFormat)
      : "";
  return { start: startStr, end: endStr };
}

/**
 * @deprecated Use getScheduleTimeDisplayInTz(schedule, userTimezone) with user profile or session TZ.
 * Get display start/end for a schedule in a time zone (legacy: used schedule/site TZ).
 */
export function getScheduleTimeDisplay(schedule, timeFormat = "h:mm A") {
  const tz = DEFAULT_USER_TIMEZONE;
  return getScheduleTimeDisplayInTz(schedule, tz, timeFormat);
}

/**
 * Get display start/end time strings for a schedule in the user's profile time zone.
 * Pass userTimezone from auth context (or schedule-screen session override when on schedule screen).
 * Prefers startDateTime/endDateTime (UTC) when present; falls back to timeStart/timeEnd.
 * @param {{ startDateTime?: string, endDateTime?: string, timeStart?: string, timeEnd?: string }} schedule
 * @param {string} [userTimezone] - IANA time zone (user profile or session override); defaults to DEFAULT_USER_TIMEZONE
 * @param {string} [timeFormat] - moment format for time; default "h:mm A"
 * @returns {{ start: string, end: string }}
 */
export function getScheduleTimeDisplayInLocal(schedule, userTimezone, timeFormat = "h:mm A") {
  const tz = userTimezone || DEFAULT_USER_TIMEZONE;
  return getScheduleTimeDisplayInTz(schedule, tz, timeFormat);
}

/**
 * Common IANA time zones for site/schedule dropdowns.
 * Use these only; do not store numeric offsets or abbreviations.
 */
export const COMMON_IANA_TIMEZONES = [
  "America/Los_Angeles",
  "America/Denver",
  "America/Phoenix",
  "America/Chicago",
  "America/New_York",
  "America/Anchorage",
  "Pacific/Honolulu",
  "America/Toronto",
  "America/Vancouver",
  "Asia/Manila",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Europe/London",
  "Europe/Paris",
  "UTC",
];
